import { Link, useNavigate } from '@tanstack/react-router'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@tracker/ui'
import { AlertCircle, LogIn } from 'lucide-react'
import { useId, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

export function Login() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const emailId = useId()
  const passwordId = useId()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email || !password) {
      setError('Please enter your email and password')
      return
    }

    setIsLoading(true)

    try {
      // Demo mode: accept any login for testing
      if (
        window.location.hostname === 'localhost' ||
        window.location.hostname.includes('pages.dev')
      ) {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Get demo account or create a default one
        const demoPublisher = localStorage.getItem('demoPublisher')
        const publisher = demoPublisher
          ? JSON.parse(demoPublisher)
          : {
              id: 'demo-user',
              email: email,
              username: email.split('@')[0],
              status: 'active',
              tier: 'starter',
              totalEarnings: 1250.5,
              pendingPayment: 450.0,
            }

        // Store demo session
        localStorage.setItem('publisherToken', `demo-token-${Date.now()}`)
        localStorage.setItem('publisher', JSON.stringify(publisher))

        navigate({ to: '/dashboard' })
      } else {
        await login(email, password)
        navigate({ to: '/dashboard' })
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Invalid email or password. The API is currently in development.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4 mx-auto">
            <LogIn className="w-8 h-8 text-indigo-600" />
          </div>
          <CardTitle className="text-3xl font-bold">Welcome Back</CardTitle>
          <CardDescription>Sign in to your publisher account</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor={emailId}>Email Address</Label>
              <Input
                id={emailId}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={passwordId}>Password</Label>
              <Input
                id={passwordId}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
              />
            </div>

            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
